import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Heart, Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getAllProducts } from "../api/productApi";
import config from "../config";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const FeaturedProducts = () => {
  const { t, i18n } = useTranslation();
  const [featuredProducts, setProducts] = useState([]);
  const { selectedBranch } = useAuth();
  useEffect(() => {
    async function getProducts() {
      const data = await getAllProducts({});
      setProducts(data);
    }
    getProducts();
  }, []);
  const accent = ["bg-rose-50", "bg-blue-50", "bg-amber-50"];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header Section */}
      <motion.div
        className="pt-16 pb-12 px-6 text-center"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="text-4xl md:text-5xl font-light text-neutral-800">
        {i18n.language === "ar"
                      ? "مجموعات مميزة"
                      : (<>Featured <span className="font-medium">Collections</span></>)}
        </h2>
        <p className="mt-4 text-neutral-600 max-w-2xl mx-auto">
          {t('featured.desc')}
        </p>
      </motion.div>

      {/* Products Grid */}
      <motion.div
        className="container mx-auto px-6 pb-10"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {featuredProducts.map((product, index) => {
          const inventory = product.storeInventory?.find(
            (inv) => inv.branch_id === selectedBranch?.branch_id
          );
          const price = inventory ? inventory.price : product.default_price;
          const currency = inventory
            ? inventory.currency
            : product.default_currency;
          const deliveryTime = selectedBranch
            ? inventory && inventory.quantity > 0
              ? `${selectedBranch?.delivery_time} days`
              : `${selectedBranch?.delivery_time_out_stock} days`
            : "Select your Country";
          return (
            <motion.div
              key={product.product_id}
              variants={itemVariants}
              className="mb-10 last:mb-0"
            >
              <div
                className={`
              relative max-w-6xl mx-auto
              ${index % 2 === 0 ? "lg:ml-0 lg:mr-auto" : "lg:ml-auto lg:mr-0"}
            `}
              >
                {/* Background Accent */}
                <div
                  className={`absolute inset-0 bg-neutral-100 shadow-lg rounded-3xl transform 
                ${index % 2 === 0 ? "rotate-2" : "-rotate-2"}
                transition-transform duration-300 group-hover:rotate-0`}
                />

                {/* Content Container */}
                <div
                  className={`
                relative group grid lg:grid-cols-2 gap-8 p-8 
                ${index % 2 === 0 ? "lg:pr-24" : "lg:pl-24"}
              `}
                >
                  {/* Image Section */}
                  <div
                    className={`
                  relative overflow-hidden rounded-2xl w-full max-w-[80%]
                  ${index % 2 === 0 ? "lg:order-1" : "lg:order-2"}
                `}
                  >
                    <motion.div
                      className="aspect-[3/4] "
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.4 }}
                    >
                      <img
                        src={`${config.apiUrl}${product.productImages[0]?.image_url}`}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    </motion.div>
                    {/* Floating Elements */}

{product.status ==="inactive" && (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="absolute inset-0 bg-black/60 flex items-center justify-center z-10 rounded-2xl"
  >
    <span className="text-white text-2xl lg:text-3xl font-bold uppercase tracking-widest">
      {t('SoldOut') || 'SOLD OUT'}
    </span>
  </motion.div>
)}
                  </div>
                  <div
                    className={`
                  space-y-6 flex flex-col justify-center
                  ${
                    index % 2 === 0
                      ? "lg:order-2 lg:pl-8"
                      : "lg:order-1 lg:pr-8"
                  }
                `}
                  >
                    <motion.div
                      className="space-y-4"
                      initial={{ opacity: 0, x: index % 2 === 0 ? 20 : -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.2 }}
                    >
                      <h3 className="text-3xl font-light text-neutral-800">
                        {i18n.dir() === "rtl" ? product.ar_name : product.name}
                      </h3>
                      <p className="text-neutral-600 leading-relaxed">
                        {i18n.dir() === "rtl"
                          ? product.ar_description
                          : product.description}
                      </p>
                    </motion.div>

                    <div className="space-y-4">
                      <div className="text-2xl font-light text-neutral-800">
                        {price.toFixed(2)} {currency}
                      </div>
                      <div className="flex gap-4">
                        <Link
                          to={`/product/${
                            product.product_id
                          }/${encodeURIComponent(product.name)}`}
                          className="flex-1 inline-flex items-center justify-center gap-2 bg-neutral-900 text-white px-6 py-3 rounded-full hover:bg-neutral-800 transition-colors"
                        >
                          {t('DiscoverMore')}
                          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </Link>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                          className="p-3 rounded-full border border-neutral-200 hover:border-neutral-300 transition-colors"
                        >
                          <Heart className="w-5 h-5" />
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
};

export default FeaturedProducts;
